import React, { 
  createContext, // usamos para criar variáveis de contexto! 
  useCallback, 
  useState, 
  useContext 
} from 'react';

import { isBefore } from 'date-fns';

import api from '~/shared/services/api';
import askNotificationPermission from '~/utils/askNotificationPermission';

/**
 * Crinado interface que representa os dados da jornada de trabalho do usuário MyIpac
 */
interface TodayShift {
  primeiro_turno_entrada: string;
  primeiro_turno_saida: string;
  segundo_turno_entrada: string;
  segundo_turno_saida: string;
  tempo_disponivel: null;
  full: number;
  total: string;
}

/**
 *  Criando interface que representa a estrutura dos dados de um usuário
 */
interface User {
  avatar: string;
  avatar_url: string;
  cover: string;
  cover_url: string;
  created_at: string;
  email: string;
  id: string;
  name: string;
  updated_at: string;
  responsibility_id?: string;
  responsibility?: string;
  department_id?: string;
  department?: string;
  has_registered_shift: boolean;
  today_shift: TodayShift | null;
  permissions: string[] | null;
  company_id?: number;
  first_access?: boolean;
  is_subscribed?: boolean;
  allow_notification?: boolean;
}

/**
 * Criando estrutura para os objetos que indicam o estado atual do usuário do MyIpac
 */
interface AuthState {
  token: string; // atenção que aqui definimos o token do usuário
  user: User; // aqui temos um objeto com todos os dados do usuário!
}

/**
 * Criando estrutura para os objetos que representam as credenciais 
 * enviados pelo usuário.
 */
interface SignInCredentials {
  email: string;
  password: string;
  is_subscribed: boolean;
  company_id?: number;
  googleId?: string;
  imageUrl?: string;
}

/**
 * Criando Dados de contexto do valor em um usuário
 * vamos usar para justamente validar informações do usuário 
 * e obter completamente todos os dados do usuário e validar regra de negócio
 */
interface AuthContextData {
  user: User;
  completedTourBSC: boolean;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(updateData: Partial<User>): Promise<void>;
  finishTourBSC(): void;
}

// Criando nossa variável que armazena o contexto com os dados do usuário... 
// atenção! ele inicialmente é instanciado vazio!
const AuthContext = createContext<AuthContextData>({} as AuthContextData);

/**
 * Crinado componente que vai ser o nosso provedor de autorizacao e autentificação de usuário no front-end
 * todos os filhos dele terá acesso aos props desta componente
 * @param param0 
 * @returns 
 */
const AuthProvider: React.FC = ({ children }) => {

  const [completedTourBSC, setCompletedTourBSC] = useState(
    localStorage.getItem('tourBsc') === 'true',
  );

  // Usa função de inicialização para ler os dados do localStorage apenas uma vez na montagem do componente
  const [data, setData] = useState<AuthState>(() => {
    
    // Obtem o item (valor) no localStorage, neste caso, obtemos o token antigo
    const token = localStorage.getItem('@MyIPAC:token');
    
    // Obtemo o token expirado.. 
    const expires_token = localStorage.getItem('@MyIPAC:expires_token');
    
    // obtemos informacoes de usuário do Localstorage... basicamente um objeto com todas as informacoes do usuario
    const user = localStorage.getItem('@MyIPAC:user');

    // Se não existir data de expiração do token
    if (!expires_token) {
      // retornamos um objeto vazio -> Type Assertion (afirmação de tipo).
      return {} as AuthState;
    }

    // verifica se a data atual ainda é antes da data de expiração salva em expires_token
    const unexpiredToken = isBefore(
      new Date(),
      new Date(JSON.parse(expires_token)),
    );

    // validar se existe token, se existe usuario com suas permissoes e se o token dele nao esta expirado....
    // Se todas forem verdadeiras, o usuário é considerado autenticado e válido.
    if (token && user && unexpiredToken) {

      // Adiciona o token JWT (ou outro tipo de token) no header Authorization para todas as requisições futuras da API.
      // Sintaxe Bearer ${token} é padrão para autenticação JWT.
      api.defaults.headers.authorization = `Bearer ${token}`;
      
      // O user estava salvo como string JSON, então é transformado em objeto.
      const parseUser = JSON.parse(user);
      
      // O header company é configurado com company_id do usuário, provavelmente para que a API saiba qual empresa o usuário pertence.
      api.defaults.headers.company = parseUser.company_id;

      // Retorna um objeto contendo o token e os dados do usuário.
      return { token, user: JSON.parse(user) };
    }

    // se nao existe informações sobre a data de expiracao do token
    // está removendo os dados de autenticação quando o token expirou ou não é válido.
    if (!unexpiredToken) {
      localStorage.removeItem('@MyIPAC:token');
      localStorage.removeItem('@MyIPAC:expires_token');
      localStorage.removeItem('@MyIPAC:user');
    }

    // caso nenhuma das duas validações de certo, vamos retornar um objeto vazio!
    return {} as AuthState;
  });

  // Funcao  anônima que obtem dados do login -> lembrando que usamos useCallback para armazenar a function na cache do navegador
  // e a funcao só muda o valor que ela retorna se um de seus depedentes },[]}; mudar...
  const signIn = useCallback(
    async ({
      email,
      password,
      company_id,
      is_subscribed,
      googleId,
      imageUrl,
    }) => {
      const response = await api.post('sessions', {
        email,
        password,
        company_id,
        googleId,
        imageUrl,
      });

      const {
        token,
        expires_in,
        user,
        responsibility,
        permissions,
        department,
        today_shift,
        has_registered_shift,
      } = response.data;

      const formattedUser = {
        ...user,
        avatar: user.avatar ? user.avatar : user.google_image,
        responsibility: responsibility?.name,
        responsibility_id: responsibility?.id,
        department_id: department?.id,
        department: department?.name,
        company_id: responsibility?.company_id,
        permissions,
        is_subscribed,
        has_registered_shift,
        today_shift,
      };

      const expires_token = expires_in;

      localStorage.setItem('@MyIPAC:token', token);
      localStorage.setItem(
        '@MyIPAC:expires_token',
        JSON.stringify(expires_token),
      );

      /** *** TESTE ***** */
      if (!localStorage.getItem('tourBsc')) {
        localStorage.setItem('tourBsc', 'false');
      }

      /* Os seguintes cabeçalhos geram preflight por não se encaixarem como requisição simples. Agrega-se a isso o fato
      de utilizar o header Content-Type: application/json, apesar de que não é necessário para GET requests */

      api.defaults.headers.authorization = `Bearer ${token}`;
      api.defaults.headers.company = company_id;

      // console.log('solicitando autorização de notificação');

      /* Após o login bem sucedido se o usuário ainda não definiu se é para permitir as notificações, solicitamos a permissão */
      const permission = await askNotificationPermission();

      formattedUser.allow_notification = permission === 'granted';

      // colocando no objeto user armazenado no localStorage um novo atributo...
      localStorage.setItem('@MyIPAC:user', JSON.stringify(formattedUser));

      // atualizando dados do usuário
      setData({ token, user: formattedUser });
    },
    [], // a funcao é recalculada apenas uma vez!
  );

  const signOut = useCallback(() => {
    /* Auth */
    localStorage.removeItem('@MyIPAC:token');
    localStorage.removeItem('@MyIPAC:expires_token');
    localStorage.removeItem('@MyIPAC:user');

    /* Notificação */
    localStorage.removeItem('@MyIPAC:notification');

    /* Atividades */
    localStorage.removeItem('@MyIPAC:timerLastUpdate');
    localStorage.removeItem('@MyIPAC:timer');

    setData({} as AuthState);
  }, []);

  /**
   * Atualiza os dados do usuário no estado de autenticação e no localStorage.
   *
   * Esta função mescla os dados atuais do usuário (`data.user`) com os novos dados
   * passados em `updateData`. Em seguida, atualiza o localStorage e o estado global
   * de autenticação para refletir essas mudanças.
   *
   * Fluxo de funcionamento:
   * 1. Recebe um objeto `updateData` contendo as alterações nos dados do usuário.
   * 2. Cria um novo objeto `userUpdate` combinando os dados antigos com as alterações.
   *    - Qualquer campo presente em `updateData` sobrescreve o valor antigo.
   * 3. Salva `userUpdate` no localStorage com a chave '@MyIPAC:user'.
   * 4. Atualiza o estado `data` no AuthProvider, mantendo o token atual e atualizando
   *    o usuário para `userUpdate`.
   *
   * Exemplo de uso:
   * ```ts
   * updateUser({ name: "Novo Nome", avatar: "url_novo_avatar" });
   * ```
   *
   * @param {User} updateData - Objeto com os campos do usuário que devem ser atualizados.
   */
  const updateUser = useCallback(
    async (updateData: User) => {
      const userUpdate = {
        ...data.user,
        ...updateData,
      };

      localStorage.setItem('@MyIPAC:user', JSON.stringify(userUpdate));

      setData({
        token: data.token,
        user: userUpdate,
      });
    },
    [setData, data], // a funcao muda quando os dados do usuario sao atualizadas ou quando se tem um valor de usuario novo
  );

  const finishTourBSC = useCallback(() => {
    setCompletedTourBSC(true);
  }, []);
  
  // atualizando valores com base na variável global de contexto! 
  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signOut,
        updateUser,
        finishTourBSC,
        completedTourBSC,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
